import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../context/AuthProvider";
import { AssetTableItem } from "../../types/types";
import { useToast } from "../ui/Toast";
import { Link } from "react-router-dom";
import "./CustomAssetReferenceEditor.css";
import Tooltip from "../ui/Tooltip";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface CustomAssetReferenceEditorProps {
  value?: string;
  onChange?: (_content: string) => void;
  placeholder?: string;
  campaignId?: number; // Optional campaign ID to filter assets by campaign
  readOnly?: boolean; // Flag to toggle between edit and view modes
  navigationUrlFormat?: string; // Optional URL format for navigation, e.g., "/protected/assets/:id" or "/assets/:id/view"
  showReferencedAssets?: boolean; // Flag to show referenced assets in the editor
  hideTooltip?: boolean; // Flag to hide tooltips on asset references
}

interface AssetReference {
  id: number;
  name: string;
  description?: string;
}

interface AssetDetail {
  id: number;
  name: string;
  description?: string;
  type?: string;
  category?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  owner?: string;
  world?: string;
  isLoading?: boolean;
  error?: string;
}

const CustomAssetReferenceEditor: React.FC<CustomAssetReferenceEditorProps> = ({
  value = "",
  onChange,
  placeholder,
  campaignId,
  readOnly = false,
  navigationUrlFormat,
  showReferencedAssets = false,
  hideTooltip = false,
}) => {
  const [text, setText] = useState("");
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  // We're not using searchTerm directly but it's used in the filtering logic
  const [, setSearchTerm] = useState("");
  const [assets, setAssets] = useState<AssetTableItem[]>([]);
  const [filteredAssets, setFilteredAssets] = useState<AssetTableItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [referencedAssets, setReferencedAssets] = useState<AssetReference[]>([]);
  const [assetDetails, setAssetDetails] = useState<Record<number, AssetDetail>>({});
  const [, setActiveAssetId] = useState<number | null>(null);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<HTMLDivElement>(null);
  const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null);
  const auth = useAuth();
  const { showToast } = useToast();

  // Handle initial value
  useEffect(() => {
    // Convert storage/legacy format to internal editing format
    if (value && (value.includes('<span class="asset-reference"') || value.includes("[[asset:"))) {
      const convertedText = htmlToInternalFormat(value);
      setText(convertedText);
      extractReferencedAssets(convertedText);
    } else {
      setText(value);
      extractReferencedAssets(value);
    }
  }, [value, readOnly]);

  const getAssets = useCallback(
    async (campaignId?: number) => {
      const endpoint = campaignId
        ? `/api/protected/campaigns/${campaignId}/assets`
        : "/api/protected/assets";

      if (campaignId) {
        return await fetch(endpoint, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.token()}`,
          },
        });
      }

      return await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token()}`,
        },
        body: JSON.stringify({
          page: 1,
          pageSize: 100, // Fetch a reasonable number of assets for suggestions
          name: "",
          type: "",
          category: "",
          world: "",
        }),
      });
    },
    [auth]
  );

  // Fetch assets for suggestions and reference data
  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAssets(campaignId);

      if (response.ok) {
        const data = await response.json();
        if (campaignId && Array.isArray(data)) {
          // Campaign assets endpoint returns CampaignAssetResponse[] directly.
          // Override the id with the campaign asset ID so inserted references
          // embed campaign asset IDs (not personal asset IDs).
          setAssets(
            data.map((ca: { id: number; asset: AssetTableItem }) => ({ ...ca.asset, id: ca.id }))
          );
        } else {
          setAssets(data.assets || []);
        }
      } else {
        showToast({
          type: "error",
          message: "Failed to load assets for references",
        });
      }
    } catch (error) {
      console.error("Error fetching assets:", error);
      showToast({
        type: "error",
        message: "Something went wrong fetching assets",
      });
    } finally {
      setLoading(false);
    }
  }, [campaignId, getAssets, showToast]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  // Handle text input changes in edit mode
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setText(newText);

    if (onChange) {
      // Convert to HTML format for storage
      onChange(internalToHtmlFormat(newText));
    }

    // Check for asset reference trigger
    const position = e.target.selectionStart;
    setCursorPosition(position);

    // Check if we're in the process of referencing an asset
    const textBeforeCursor = newText.substring(0, position);

    const updateDropdownPos = () => {
      if (textareaRef.current) {
        const rect = textareaRef.current.getBoundingClientRect();
        setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
    };

    // ── ">>" trigger ──────────────────────────────────────────────────────────
    const match = Array.from(textBeforeCursor.matchAll(/>>/g));
    const matchClosed = Array.from(textBeforeCursor.matchAll(/<</g));

    const noOpen = match.length;
    const noClosed = matchClosed.length;

    if (noOpen > noClosed) {
      // find the open '>>' that is not closed
      const openIndex = match.findIndex((m) => {
        const index = m.index || 0;
        return !matchClosed.some((mc) => mc.index && mc.index > index);
      });

      if (openIndex !== -1) {
        const term = match[0].input.substring(match[openIndex].index + 2).trim();
        setSearchTerm(term);
        setShowSuggestions(true);
        updateDropdownPos();
        const filtered = assets.filter((asset) =>
          asset.name.toLowerCase().includes(term.toLowerCase())
        );
        setFilteredAssets(filtered);
        extractReferencedAssets(newText);
        return;
      }
    }

    // ── "[[" trigger ──────────────────────────────────────────────────────────
    const bracketOpenMatches = Array.from(textBeforeCursor.matchAll(/\[\[/g));
    const bracketCloseMatches = Array.from(textBeforeCursor.matchAll(/\]\]/g));

    if (bracketOpenMatches.length > bracketCloseMatches.length) {
      const lastOpen = bracketOpenMatches[bracketOpenMatches.length - 1];
      const term = textBeforeCursor.slice((lastOpen.index ?? 0) + 2).trim();
      setSearchTerm(term);
      setShowSuggestions(true);
      updateDropdownPos();
      const filtered = assets.filter((asset) =>
        asset.name.toLowerCase().includes(term.toLowerCase())
      );
      setFilteredAssets(filtered);
      extractReferencedAssets(newText);
      return;
    }

    setShowSuggestions(false);
    setDropdownPos(null);

    // Extract referenced assets for display
    extractReferencedAssets(newText);
  };

  // Convert any storage/legacy format to the internal editing format >>Name{ID}<<
  const htmlToInternalFormat = (content: string): string => {
    if (!content) return "";

    // Convert new markdown storage format [[asset:ID|Name]] → >>Name{ID}<<
    if (content.includes("[[asset:")) {
      return content.replace(
        /\[\[asset:(\d+)\|([^\]]*)\]\]/g,
        (_, id, name) => `>>${name}{${id}}<<`
      );
    }

    // Convert legacy HTML spans
    if (content.includes('<span class="asset-reference"')) {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, "text/html");
        let result = content;

        const references = doc.querySelectorAll(".asset-reference");
        references.forEach((ref) => {
          const assetId = ref.getAttribute("data-asset-id");
          const assetName = ref.textContent || "";
          if (assetId) {
            const htmlSpan = `<span class="asset-reference" data-asset-id="${assetId}">${assetName}</span>`;
            result = result.replace(htmlSpan, `>>${assetName}{${assetId}}<<`);
          }
        });

        if (result.includes("&lt;")) {
          result = result.replace(/&lt;/g, "<").replace(/&gt;/g, ">");
        }

        return result;
      } catch (error) {
        console.error("Error converting HTML to internal format:", error);
        return content;
      }
    }

    return content;
  };

  // Convert internal editing format >>Name{ID}<< to the storage format [[asset:ID|Name]]
  const internalToHtmlFormat = (text: string): string => {
    if (!text) return "";

    return text.replace(/>>([^{]+)\{(\d+)\}<</g, (_, name, id) => `[[asset:${id}|${name}]]`);
  };

  // Convert to display format function removed as we now use React elements

  // Extract referenced assets from text
  const extractReferencedAssets = (text: string) => {
    if (!text) {
      setReferencedAssets([]);
      return;
    }

    const regex = />>([^{]+)\{(\d+)\}<</g;
    let match;
    const references: AssetReference[] = [];

    while ((match = regex.exec(text)) !== null) {
      references.push({
        name: match[1],
        id: parseInt(match[2]),
      });
    }

    setReferencedAssets(references);
  };

  // Handle selecting an asset from suggestions
  const handleSelectAsset = (asset: AssetTableItem) => {
    if (cursorPosition !== null && textareaRef.current) {
      const beforeText = text.substring(0, cursorPosition);
      const afterText = text.substring(cursorPosition);

      // Find the most recent active trigger (>> or [[)
      const gtTriggerPos = beforeText.lastIndexOf(">>");
      const bracketTriggerPos = beforeText.lastIndexOf("[[");
      const triggerPos = Math.max(gtTriggerPos, bracketTriggerPos);

      if (triggerPos !== -1) {
        const newText =
          beforeText.substring(0, triggerPos) + `>>${asset.name}{${asset.id}}<<` + afterText;

        setText(newText);

        if (onChange) {
          onChange(internalToHtmlFormat(newText));
        }

        // Extract referenced assets again
        extractReferencedAssets(newText);
      }
    }

    setShowSuggestions(false);
    setDropdownPos(null);
  };

  // Update dropdown position on scroll/resize while open
  useEffect(() => {
    if (!showSuggestions) return;
    const updatePos = () => {
      if (textareaRef.current) {
        const rect = textareaRef.current.getBoundingClientRect();
        setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
      }
    };
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [showSuggestions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setDropdownPos(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    const timer = tooltipTimerRef.current;
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  const createNaviagtionUrl = (navigationUrlFormat: string, id: number): string | undefined => {
    let url;
    if (navigationUrlFormat) {
      // Replace :id placeholder with the actual asset ID
      url = navigationUrlFormat.replace(":id", id.toString());

      // Replace :campaignId placeholder if a campaign ID is available
      if (campaignId) {
        url = url.replace(":campaignId", campaignId.toString());
      }
    }
    return url;
  };

  // Function to fetch asset details
  const fetchAssetDetails = useCallback(
    async (assetId: number) => {
      // If we already have details or are currently loading, don't fetch again
      if (assetDetails[assetId] && !assetDetails[assetId].isLoading) return;

      // Set loading state for this asset
      setAssetDetails((prev) => ({
        ...prev,
        [assetId]: { id: assetId, name: "", isLoading: true },
      }));

      try {
        // Determine endpoint based on whether a campaignId is provided
        const endpoint = campaignId
          ? `/api/protected/campaigns/${campaignId}/assets/${assetId}`
          : `/api/protected/assets/asset/${assetId}`;

        const response = await fetch(endpoint, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.token()}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setAssetDetails((prev) => ({
            ...prev,
            [assetId]: {
              ...data,
              isLoading: false,
            },
          }));
        } else {
          setAssetDetails((prev) => ({
            ...prev,
            [assetId]: {
              id: assetId,
              name: "Unknown Asset",
              description: "Could not load asset details",
              isLoading: false,
              error: `Error: ${response.status}`,
            },
          }));
        }
      } catch (error) {
        console.error("Error fetching asset details:", error);
        setAssetDetails((prev) => ({
          ...prev,
          [assetId]: {
            id: assetId,
            name: "Unknown Asset",
            description: "Error loading asset details",
            isLoading: false,
            error: String(error),
          },
        }));
      }
    },
    [auth, campaignId, assetDetails]
  );

  // Component for tooltip content
  const AssetTooltipContent = useCallback(
    ({ assetId }: { assetId: number }) => {
      // Get asset details from state or trigger fetch if not loaded
      const details = assetDetails[assetId];

      // Fetch details if not already loaded - using effect inside callback is not allowed
      // Instead, we'll do this check directly and call fetchAssetDetails if needed
      if (!details || (!details.isLoading && !details.error && !details.name)) {
        // Call fetchAssetDetails directly
        fetchAssetDetails(assetId);
      }

      if (!details) {
        return <div className="loading-tooltip">Loading asset details</div>;
      }

      if (details.isLoading) {
        return <div className="loading-tooltip">Loading asset details</div>;
      }

      if (details.error) {
        return <div className="tooltip-error">Error: {details.error}</div>;
      }

      // Helper function to safely render asset description in tooltip.
      // The description is user-authored, so we HTML-escape it first and only then
      // turn recognized asset-reference markers into links — any other markup the
      // author included (e.g. <img onerror=...>) is neutralized as inert text.
      const renderHtmlContent = (content: string) => {
        const escapeHtml = (s: string) =>
          s
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;");

        const processedContent = escapeHtml(content)
          // New marker form: [[asset:123|Name]]
          .replace(
            /\[\[asset:(\d+)\|([^\]]*)\]\]/g,
            (_, assetId, assetName) =>
              `<a href="#" class="asset-reference-link" data-asset-id="${assetId}">${assetName}</a>`
          )
          // Legacy span form (now in its escaped representation)
          .replace(
            /&lt;span class=&quot;asset-reference&quot; data-asset-id=&quot;(\d+)&quot;&gt;([^&]+)&lt;\/span&gt;/g,
            (_, assetId, assetName) =>
              `<a href="#" class="asset-reference-link" data-asset-id="${assetId}">${assetName}</a>`
          );

        return { __html: processedContent };
      };

      return (
        <div className="tooltip-content">
          <div className="tooltip-title">{details.name}</div>

          {details.type && (
            <div className="tooltip-detail">
              <span className="tooltip-label">Type:</span>
              <span className="tooltip-value">{details.type}</span>
            </div>
          )}

          {details.category && (
            <div className="tooltip-detail">
              <span className="tooltip-label">Category:</span>
              <span className="tooltip-value">{details.category}</span>
            </div>
          )}

          {details.world && (
            <div className="tooltip-detail">
              <span className="tooltip-label">World:</span>
              <span className="tooltip-value">{details.world}</span>
            </div>
          )}

          {details.owner && (
            <div className="tooltip-detail">
              <span className="tooltip-label">Owner:</span>
              <span className="tooltip-value">{details.owner}</span>
            </div>
          )}

          {details.description && (
            <div
              className="tooltip-description"
              dangerouslySetInnerHTML={renderHtmlContent(details.description)}
            />
          )}
        </div>
      );
    },
    [assetDetails, fetchAssetDetails]
  );

  // Handle hovering over an asset reference
  const handleAssetHover = useCallback(
    (assetId: number) => {
      setActiveAssetId(assetId);

      // If we don't have details for this asset yet, fetch them
      if (!assetDetails[assetId]) {
        fetchAssetDetails(assetId);
      }
    },
    [assetDetails, fetchAssetDetails]
  );

  if (readOnly) {
    // Read-only view mode with rendered markdown and highlighted references
    const renderTextWithHighlightedReferences = () => {
      if (!text) return null;

      // text is always in internal >>Name{ID}<< format after useEffect conversion
      const regex = />>([^{]+)\{(\d+)\}<</g;
      const parts: React.ReactNode[] = [];
      let lastIndex = 0;
      let match;

      while ((match = regex.exec(text)) !== null) {
        // Add the reference as a highlighted span
        const [fullMatch, assetName, assetId] = match;
        const id = parseInt(assetId);

        // Add text before the reference — render as Markdown
        if (match.index > lastIndex) {
          const segment = text.substring(lastIndex, match.index);
          parts.push(
            <ReactMarkdown
              key={`text-${lastIndex}`}
              remarkPlugins={[remarkGfm]}
              components={{
                h3: ({ children, ...props }) => (
                  <h3 {...props} className="text-base font-semibold text-zinc-100 mt-3 mb-1">
                    {children}
                  </h3>
                ),
                h2: ({ children, ...props }) => (
                  <h2 {...props} className="text-lg font-semibold text-zinc-100 mt-4 mb-1.5">
                    {children}
                  </h2>
                ),
                ul: ({ children, ...props }) => (
                  <ul {...props} className="list-disc list-inside my-1 ml-2 text-zinc-300">
                    {children}
                  </ul>
                ),
                ol: ({ children, ...props }) => (
                  <ol {...props} className="list-decimal list-inside my-1 ml-2 text-zinc-300">
                    {children}
                  </ol>
                ),
                li: ({ children, ...props }) => (
                  <li {...props} className="text-zinc-300 my-0.5">
                    {children}
                  </li>
                ),
                p: ({ children, ...props }) => (
                  <p {...props} className="text-zinc-300 my-0.5">
                    {children}
                  </p>
                ),
                strong: ({ children, ...props }) => (
                  <strong {...props} className="text-zinc-100 font-semibold">
                    {children}
                  </strong>
                ),
                em: ({ children, ...props }) => (
                  <em {...props} className="text-zinc-300 italic">
                    {children}
                  </em>
                ),
              }}
            >
              {segment}
            </ReactMarkdown>
          );
        }

        if (hideTooltip) {
          parts.push(
            <span
              key={`ref-${match.index}`}
              className="asset-reference-display"
              data-asset-id={id}
              onMouseEnter={() => handleAssetHover(id)}
            >
              <Link to={createNaviagtionUrl(navigationUrlFormat!, id)!}>{assetName}</Link>
            </span>
          );
        } else {
          parts.push(
            <Tooltip
              key={`ref-${match.index}`}
              content={<AssetTooltipContent assetId={id} />}
              position="top"
              className="asset-tooltip"
              delay={500}
            >
              <span
                className="asset-reference-display"
                data-asset-id={id}
                onMouseEnter={() => handleAssetHover(id)}
              >
                <Link to={createNaviagtionUrl(navigationUrlFormat!, id)!}>{assetName}</Link>
              </span>
            </Tooltip>
          );
        }

        lastIndex = match.index + fullMatch.length;
      }

      // Add any remaining text — render as Markdown
      if (lastIndex < text.length) {
        const segment = text.substring(lastIndex);
        parts.push(
          <ReactMarkdown
            key="text-end"
            remarkPlugins={[remarkGfm]}
            components={{
              h3: ({ children, ...props }) => (
                <h3 {...props} className="text-base font-semibold text-zinc-100 mt-3 mb-1">
                  {children}
                </h3>
              ),
              h2: ({ children, ...props }) => (
                <h2 {...props} className="text-lg font-semibold text-zinc-100 mt-4 mb-1.5">
                  {children}
                </h2>
              ),
              ul: ({ children, ...props }) => (
                <ul {...props} className="list-disc list-inside my-1 ml-2 text-zinc-300">
                  {children}
                </ul>
              ),
              ol: ({ children, ...props }) => (
                <ol {...props} className="list-decimal list-inside my-1 ml-2 text-zinc-300">
                  {children}
                </ol>
              ),
              li: ({ children, ...props }) => (
                <li {...props} className="text-zinc-300 my-0.5">
                  {children}
                </li>
              ),
              p: ({ children, ...props }) => (
                <p {...props} className="text-zinc-300 my-0.5">
                  {children}
                </p>
              ),
              strong: ({ children, ...props }) => (
                <strong {...props} className="text-zinc-100 font-semibold">
                  {children}
                </strong>
              ),
              em: ({ children, ...props }) => (
                <em {...props} className="text-zinc-300 italic">
                  {children}
                </em>
              ),
            }}
          >
            {segment}
          </ReactMarkdown>
        );
      }

      return parts;
    };

    return (
      <div ref={viewerRef} className="asset-text-display">
        {renderTextWithHighlightedReferences()}
      </div>
    );
  }

  // Edit mode
  return (
    <div className="custom-asset-reference-editor">
      <textarea
        id="section-content"
        name={"text-editor"}
        ref={textareaRef}
        className="custom-asset-textarea"
        value={text}
        onChange={handleTextChange}
        placeholder={placeholder || "Type >> or [[ to reference an asset…"}
      />

      {showSuggestions &&
        dropdownPos &&
        createPortal(
          <div
            ref={suggestionsRef}
            className="asset-suggestions"
            style={{
              position: "fixed",
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              zIndex: 9999,
            }}
          >
            {loading ? (
              <div className="suggestion-loading">Loading assets...</div>
            ) : filteredAssets.length > 0 ? (
              filteredAssets.map((asset) => (
                <div
                  key={asset.id}
                  className="asset-suggestion-item"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelectAsset(asset);
                  }}
                >
                  <div className="asset-suggestion-name">{asset.name}</div>
                  <div className="asset-suggestion-description">
                    {asset.description
                      ? asset.description.length > 60
                        ? asset.description.substring(0, 60) + "..."
                        : asset.description
                      : "No description"}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-suggestions">No matching assets found</div>
            )}
          </div>,
          document.body
        )}

      {showReferencedAssets && referencedAssets.length > 0 && (
        <div className="referenced-assets">
          <div className="referenced-assets-title">Referenced Assets:</div>
          <div className="referenced-assets-list">
            {referencedAssets.map((asset) => (
              <Tooltip
                key={asset.id}
                content={<AssetTooltipContent assetId={asset.id} />}
                position="top"
                className="asset-tooltip"
                delay={500}
              >
                <span
                  className="referenced-asset-tag"
                  onMouseEnter={() => handleAssetHover(asset.id)}
                >
                  <Link to={createNaviagtionUrl(navigationUrlFormat!, asset.id)!}>
                    {asset.name}
                  </Link>
                </span>
              </Tooltip>
            ))}
          </div>
          <div className="referenced-assets-hint">(Click on a reference to open in a new tab)</div>
        </div>
      )}
    </div>
  );
};

export default CustomAssetReferenceEditor;
